import React, { useLayoutEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import clsx from 'classnames';
import { Button } from 'rebass';
import { FiMaximize2 } from 'react-icons/fi';

import { useExpandableRow } from './rowScope';

/**
 * `min-w-0` stops a flex or grid parent from sizing this to the text's intrinsic width,
 * which in a table cell means the column tries to fit the whole value. `relative` is the
 * containing block for the floating toggle in single-line mode.
 */
const ROOT_CLASS = 'block min-w-0 relative';

/**
 * Table cells set `white-space: nowrap` via `.ellipsis`, so the value has to be allowed to
 * wrap; `whitespace-pre-wrap` additionally keeps the line breaks a panelist typed. And
 * `break-anywhere` stops one long unbroken string (a URL, a pasted id) widening the column.
 *
 * Inline rather than a block, so the toggle that follows it sits on the same line as the
 * end of the text instead of starting a new one (PFOX-1406).
 */
const CONTENT_CLASS = 'whitespace-pre-wrap break-anywhere';

/**
 * The collapsed state in a table cell: one line, cut where the line box ends, so the "…"
 * lands at the column edge. A character budget can't do that — it has no idea how wide the
 * column is (140px here, a third of a blade) — and one line is also the only way to keep
 * rows a uniform height, which 80 characters cannot promise: one line in a blade, six in a
 * grid column.
 *
 * A line clamp rather than `.ellipsis` (`white-space: nowrap`), because the panel grid is
 * `table-layout: auto`: nowrap's min-content width is the whole value on one line, which
 * widens the column to fit it and spills the text out of the grid. The clamp carries its
 * own `white-space: normal` so it survives being dropped into a cell that already has
 * `.ellipsis`. See `.line-clamp-1`.
 */
const CONTENT_LINE_CLASS = 'line-clamp-1 break-anywhere';

/**
 * Marks an open value. Carries no styling of its own — it is the hook the global sheet
 * needs to recognise a container that currently holds an expanded value (see
 * `tr:has(.expandable-content-open)` and `.expandable-hover-scope:has(...)`), because
 * both the row and the blade field are rendered by code that knows nothing about what is
 * inside them. Both centre their contents while everything is one line tall and have to
 * switch to top alignment once one of them is not, so the marker is set in either mode.
 */
const CONTENT_OPEN_CLASS = 'expandable-content-open';

/**
 * The toggle: the 24px square icon button the rest of the app already uses for a control
 * that lives inside a table row (`secondary-tiny secondary-tiny-icon` — the study grid's
 * row menu, the panel grid's "View" button), so this doesn't invent a shape of its own.
 *
 * `expandable-toggle` carries only what the variant has no opinion about: the button is
 * out of sight until the row (or blade field) around it is hovered or takes focus. That
 * reveal keys off an ancestor's state (`tr:hover`, `tr:focus-within`) which no inline
 * style can express, so it lives in the global sheet — as does the reason it fades rather
 * than using the `visibility: hidden` the sheet's other hover-reveals use: a
 * `visibility`-hidden button is not focusable, so a keyboard user could never reach it.
 */
const TOGGLE_CLASS = 'expandable-toggle secondary-tiny secondary-tiny-icon';

/**
 * Placement, inline because the button's own chrome comes from a `styled-components`
 * variant: two single-class selectors, and which of them wins depends on injection order.
 *
 * Over the trailing edge of a clamped line there is no room in the flow for the button —
 * it floats, 2px above the line's top so its 24px box is centred on the 20px line. After
 * the "…" of a budget-cut preview there is room, and it simply follows the text.
 *
 * The margin reset is load-bearing: the shared button style carries a `margin-right: 8px`
 * meant for buttons standing next to each other, which here holds the control 8px clear
 * of the value's trailing edge and leaves the text's "…" sticking out to its right.
 */
const TOGGLE_FLOATING_STYLE = { position: 'absolute', top: '-2px', right: 0, zIndex: 1, margin: 0 };
const TOGGLE_INLINE_STYLE = { margin: '0 0 0 4px', verticalAlign: 'middle' };

/**
 * The way back is a word, not the icon again.
 *
 * An icon has to float over the value to reach the same spot it opened from, and an open
 * value has no cut tail to float over — it would have to sit on live text, or the text
 * would have to leave a 24px hole on its first line for it. A link says what it does,
 * costs the value no horizontal room, and is always visible, unlike the expand icon: a
 * reader who has opened a value must be able to close it without hunting for a hover
 * target.
 *
 * `block` gives it its own line under the value, flush with the value's left edge (hence no
 * margin at all). Trailing the last word it lands in a different spot in every cell of an
 * open row — wherever that cell's text happens to end — and reads as one more word of the
 * sentence. One line down, every cell's way back is in the same place.
 */
const COLLAPSE_CLASS =
    'expandable-collapse block m-0 p-0 border-0 bg-transparent cursor-pointer fs-13 font-medium link-primary hover:underline';

/**
 * How many characters of the value stay visible while collapsed.
 *
 * 80 is one comfortable line of reading measure — Bringhurst puts a satisfactory
 * single-column line at 45-75 characters and WCAG 2.1 SC 1.4.8 caps it at 80 — which is
 * enough to carry the opening clause a reader needs to decide whether to expand.
 *
 * Don't drop much below it: a typical sentence runs 15-20 words (~90-120 characters), so a
 * shorter budget cuts before the first clause closes and the preview answers nothing.
 * Going much above it re-creates the tall-row problem this preview exists to avoid.
 */
export const DEFAULT_COLLAPSED_CHARS = 80;

/**
 * Below this fraction of the allowance a word boundary is not worth honouring —
 * cutting there would throw away most of the preview (e.g. a 60-character token
 * right after the first word), so we hard-cut instead.
 */
const MIN_WORD_BOUNDARY_RATIO = 0.5;

/**
 * Cuts `text` to at most `limit` characters, preferring the last word boundary so the
 * preview never ends mid-word — the exact complaint behind PFOX-1406. Falls back to a
 * hard cut when the allowance holds no whitespace at all (one long unbroken token) or
 * when honouring the boundary would discard most of the preview.
 *
 * @param {string} text - The full value.
 * @param {number} limit - Maximum number of characters to keep.
 * @returns {string} The cut value, without trailing whitespace and without an ellipsis.
 */
const truncateAtWordBoundary = (text, limit) => {
    const head = text.slice(0, limit);
    const lastBoundary = head.search(/\s+\S*$/);

    if (lastBoundary < limit * MIN_WORD_BOUNDARY_RATIO) return head.trimEnd();

    return head.slice(0, lastBoundary).trimEnd();
};

/**
 * Whether a clamped line can be measured at all. jsdom has no `ResizeObserver` and lays
 * nothing out, so under jest every box is 0×0 and "is it cut?" has no answer — the toggle is
 * offered unconditionally there, which is also what an old browser gets.
 */
const canMeasure = () => typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined';

/**
 * Whether a clamped element hides part of its content. `-webkit-line-clamp` keeps the full
 * content height in `scrollHeight` while `clientHeight` stops at the clamped line, so the
 * two differ exactly when a line was cut. The 1px slack absorbs sub-pixel rounding.
 * Width is checked too for the one case a clamp can't split — a single unbreakable token
 * wider than the cell.
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
const isClipped = element =>
    element.scrollHeight > element.clientHeight + 1 || element.scrollWidth > element.clientWidth + 1;

/**
 * One `ResizeObserver` shared by every clamped cell. Observing a thousand cells on a study
 * grid is a thousand entries in one observer — the browser batches their callbacks into a
 * single pass per frame — rather than a thousand observers each with their own bookkeeping.
 */
const sizeCallbacks = new WeakMap();
let sizeObserver = null;

/**
 * Calls `callback` whenever `element`'s box changes size — a dragged column, a resized
 * window, a blade opening beside the grid.
 *
 * @param {HTMLElement} element
 * @param {() => void} callback
 * @returns {() => void} Stops observing.
 */
const observeSize = (element, callback) => {
    if (!sizeObserver) {
        sizeObserver = new window.ResizeObserver(entries => {
            // React 16 renders in legacy mode (`ReactDOM.render`), where a `setState` from
            // a browser callback like this one is not batched. Without the wrapper, a cell
            // whose verdict flips commits to the DOM in the middle of the loop, so the next
            // cell's `scrollHeight` read forces a fresh layout — hundreds of interleaved
            // read/write cycles per frame while a window holding ~1000 observed cells is
            // being dragged, and the usual source of "ResizeObserver loop completed with
            // undelivered notifications". Batched, the whole callback is one render and one
            // layout.
            unstable_batchedUpdates(() => {
                entries.forEach(entry => {
                    const notify = sizeCallbacks.get(entry.target);
                    if (notify) notify();
                });
            });
        });
    }

    sizeCallbacks.set(element, callback);
    sizeObserver.observe(element);

    return () => {
        sizeCallbacks.delete(element);
        sizeObserver.unobserve(element);
    };
};

/**
 * Tracks whether the clamped element behind `ref` currently hides part of its value.
 *
 * Measured in a layout effect, so the toggle is decided before the first paint and short
 * cells never flash a toggle that then disappears. Re-measured when the value changes and
 * whenever the element is resized (see `observeSize`).
 *
 * While `enabled` is false — the value is open, so nothing is clamped — the last answer is
 * kept rather than reset. That is what tells an expanded row which of its cells were
 * actually cut: only those get a collapse button, instead of every cell in the row
 * sprouting one the moment a neighbour is opened.
 *
 * @param {React.RefObject<HTMLElement>} ref
 * @param {boolean} enabled
 * @param {string} text
 * @returns {boolean}
 */
const useIsClipped = (ref, enabled, text) => {
    const [clipped, setClipped] = useState(false);

    useLayoutEffect(() => {
        if (!enabled || !ref.current) return undefined;

        if (!canMeasure()) {
            setClipped(true);
            return undefined;
        }

        const element = ref.current;
        const measure = () => setClipped(isClipped(element));

        measure();

        return observeSize(element, measure);
    }, [enabled, text]);

    return clipped;
};

/**
 * Expand state for one value, shared with the rest of its row when there is one.
 *
 * A grid row reads as one record and its long values are long together — a screener
 * answer beside a note beside a free-text property — so design asked for one expand per
 * row rather than one per cell (PFOX-1406). Outside a row (a blade field) there is
 * nothing to co-ordinate with and the value keeps its own state.
 *
 * @returns {[boolean, (event: React.MouseEvent) => void, boolean]} Whether the value is
 *   open, the click handler, and whether a row is behind that state.
 */
const useExpandState = () => {
    const row = useExpandableRow();
    const [isExpandedAlone, setIsExpandedAlone] = useState(false);

    const isExpanded = row ? row.isExpanded : isExpandedAlone;

    const toggle = event => {
        // Cells sit inside row and link click handlers that would otherwise navigate
        // away from the value the user is trying to read.
        event.preventDefault();
        event.stopPropagation();

        if (row) {
            row.toggle();
            return;
        }

        setIsExpandedAlone(prevIsExpanded => !prevIsExpanded);
    };

    return [isExpanded, toggle, Boolean(row)];
};

/**
 * @typedef {Object} ExpandableTextProps
 * @property {string} text - The full value.
 * @property {number} [limit=80] - Characters kept while collapsed. Ignored when
 *   `singleLine` is set, which clips by width instead.
 * @property {boolean} [singleLine] - Collapse to one clamped line rather than to a
 *   character budget. For table cells — see `CONTENT_LINE_CLASS`.
 * @property {(text: string) => React.ReactNode} [renderText] - Wraps the visible text,
 *   e.g. in a link. Applied to the collapsed preview and the expanded value alike.
 * @property {string} [className] - Extra class name for the root element.
 */

/**
 * Renders a long value collapsed, with an expand/collapse icon button beside it.
 *
 * Two collapsed shapes, because a row of a grid and a field of a blade want different
 * things. By default the value is cut to a character budget, so the preview holds the
 * same amount of text however wide its container is. With `singleLine` it is instead
 * clamped to one line at whatever width the column happens to have, which is what keeps
 * rows a uniform height once several columns can expand.
 *
 * Opening and closing look different on purpose (PFOX-1406). Opening is an icon button
 * on the value's own line, not a "Show more" link under it: a label per long cell turned
 * the grid into a wall of repeated link text, and a toggle on its own line grew every row
 * it appeared in even while collapsed. It stays out of sight until the row (or blade
 * field) is hovered, so a resting grid shows nothing but data. Closing is the word
 * "Collapse" on its own line under the value — see `COLLAPSE_CLASS` — always visible,
 * and costing the open value no horizontal room. Inside a row the control does both for the whole
 * row at once, which is the shape the design prototype settled on.
 *
 * Either way the toggle appears only when there is something more to show. In budget mode
 * that is a string comparison. In `singleLine` mode it means measuring the rendered box,
 * because whether a line is cut depends on the column's width — which the user can drag —
 * so the measurement is kept live by a shared `ResizeObserver` (see `useIsClipped`). A
 * one-word value in a wide column gets no toggle; the same value in a narrow one does.
 *
 * The toggle is a sibling of the text rather than a descendant, so it stays valid and
 * clickable when `renderText` wraps the value in a link.
 *
 * @param {ExpandableTextProps} props
 * @returns {React.ReactNode}
 */
function ExpandableText({ text, limit = DEFAULT_COLLAPSED_CHARS, singleLine, renderText, className }) {
    const [isExpanded, toggle, isInRow] = useExpandState();
    const contentRef = useRef(null);
    const toggleRef = useRef(null);
    // Set only by the control that was actually clicked, so that in a row — where every
    // cell's control swaps at the same moment — the other cells don't steal the focus.
    const shouldRestoreFocus = useRef(false);
    const isClampedLineClipped = useIsClipped(contentRef, Boolean(singleLine) && !isExpanded, text);

    const handleToggle = event => {
        shouldRestoreFocus.current = true;
        toggle(event);
    };

    // The two states render different element *types* at the same position — a host
    // `<button>` when open, the rebass `Button` when closed — so React cannot reuse the
    // DOM node: it unmounts the control the user just activated and focus falls back to
    // `<body>`, leaving a keyboard user to tab in from the top of the page again. Putting
    // it back on the replacement keeps both shapes the design settled on.
    useLayoutEffect(() => {
        if (!shouldRestoreFocus.current) return;

        shouldRestoreFocus.current = false;
        if (toggleRef.current) toggleRef.current.focus();
    }, [isExpanded]);

    const isTruncatable = singleLine ? isClampedLineClipped : text.length > limit;
    // In single-line mode the full value goes into the DOM and CSS does the cutting, so
    // the "…" lands at the column edge rather than at a character we guessed at.
    const visibleText = singleLine || !isTruncatable || isExpanded ? text : `${truncateAtWordBoundary(text, limit)}…`;

    // Named for what the click does, which in a grid is the whole row — the icon alone
    // cannot say that, and a screen reader lands on one of several identical buttons.
    const label = `${isExpanded ? 'Collapse' : 'Expand'}${isInRow ? ' row' : ''}`;

    return (
        <span className={clsx(ROOT_CLASS, className)}>
            <span
                ref={contentRef}
                className={clsx(
                    singleLine && !isExpanded ? CONTENT_LINE_CLASS : CONTENT_CLASS,
                    isExpanded && isTruncatable && CONTENT_OPEN_CLASS
                )}
            >
                {renderText ? renderText(visibleText) : visibleText}
            </span>
            {isTruncatable &&
                (isExpanded ? (
                    <button
                        ref={toggleRef}
                        type="button"
                        className={COLLAPSE_CLASS}
                        aria-expanded
                        aria-label={label}
                        onClick={handleToggle}
                    >
                        Collapse
                    </button>
                ) : (
                    <Button
                        ref={toggleRef}
                        type="button"
                        variant="secondary"
                        className={TOGGLE_CLASS}
                        style={singleLine ? TOGGLE_FLOATING_STYLE : TOGGLE_INLINE_STYLE}
                        aria-expanded={false}
                        aria-label={label}
                        title={label}
                        onClick={handleToggle}
                    >
                        <FiMaximize2 />
                    </Button>
                ))}
        </span>
    );
}

/**
 * Collapses a rendered grid-cell value to one line when it is plain text.
 *
 * Whether a value needs this is decided by the value, not by the column's declared type.
 * A "Text" panel property holds up to 299 characters and a study property up to 16000 —
 * both overrun a 140px column exactly like "Long text" does — and a multi-select joins
 * its labels into one string of unbounded length. Conversely a "Long text" property
 * holding six words needs nothing. No declared type separates those cases, so the type
 * gate is gone and every text value gets the same treatment (PFOX-1406).
 *
 * Cells also render React nodes: a status badge, a "View video" link, a True/False badge.
 * Those are short by construction and cannot be re-flowed as text, so they pass through.
 * An empty cell is left alone too — there is nothing to expand, and on a wide grid most
 * of the cells are empty.
 *
 * @param {React.ReactNode} value - Whatever the cell's own render produced.
 * @returns {React.ReactNode}
 */
export const expandableCell = value =>
    typeof value === 'string' && value !== '' ? <ExpandableText singleLine text={value} /> : value;

export default ExpandableText;
